import type { PullRequestActivity, PullRequestDetail } from "@cmmd-forge/contracts";
import { ArrowDownUpIcon, CircleCheckIcon, CircleDashedIcon, CircleXIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import { Toggle, ToggleGroup } from "~/components/ui/toggle-group";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { keyedPullRequestChecks } from "./summary/pullRequestSummary.logic";

export type PullRequestDetailTabName = "summary" | "timeline" | "code";

export function PullRequestDetailTabs(props: {
  readonly activeTab: PullRequestDetailTabName;
  readonly canViewCode: boolean;
  readonly detail: PullRequestDetail;
  readonly activity: PullRequestActivity | undefined;
  readonly timelineOrder: "newest" | "oldest";
  readonly onSelect: (tab: PullRequestDetailTabName) => void;
  readonly onToggleTimelineOrder: () => void;
  readonly onPreloadCode: () => void;
}) {
  const tabs = props.canViewCode
    ? (["summary", "timeline", "code"] as const)
    : (["summary", "timeline"] as const);
  return (
    <nav
      className="flex shrink-0 items-center gap-1 overflow-x-auto border-t border-border/60 px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      aria-label="Pull request tabs"
    >
      <ToggleGroup
        size="segmented"
        variant="segmented"
        value={[props.activeTab]}
        onValueChange={(value) => {
          const tab = value[0];
          if (tab === "summary" || tab === "timeline" || tab === "code") props.onSelect(tab);
        }}
      >
        {tabs.map((tab) => (
          <Toggle
            key={tab}
            value={tab}
            aria-current={props.activeTab === tab ? "page" : undefined}
            onPointerEnter={tab === "code" ? props.onPreloadCode : undefined}
            onFocus={tab === "code" ? props.onPreloadCode : undefined}
          >
            {tab === "summary" ? "Summary" : tab === "timeline" ? "Timeline" : "Code"}
          </Toggle>
        ))}
      </ToggleGroup>
      <PullRequestTabMeta {...props} />
    </nav>
  );
}

function PullRequestTabMeta(
  props: Pick<
    Parameters<typeof PullRequestDetailTabs>[0],
    "activeTab" | "activity" | "detail" | "timelineOrder" | "onToggleTimelineOrder"
  >,
) {
  if (props.activeTab === "summary") {
    const failed = props.detail.checks.filter(
      (check) => check.status === "failure" || check.status === "cancelled",
    ).length;
    return <PullRequestChecksMeta detail={props.detail} failed={failed} />;
  }
  if (props.activeTab !== "timeline") return null;
  return (
    <div className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
      <span>{props.activity?.commentCount ?? 0} comments</span>
      <span>{props.activity?.commits.length ?? 0} commits</span>
      <Button
        type="button"
        size="xs"
        variant="ghost"
        className="h-7 px-2 text-[10px]"
        aria-label={
          props.timelineOrder === "newest"
            ? "Show oldest activity first"
            : "Show newest activity first"
        }
        onClick={props.onToggleTimelineOrder}
      >
        <ArrowDownUpIcon aria-hidden="true" className="size-3" />
        {props.timelineOrder === "newest" ? "Newest first" : "Oldest first"}
      </Button>
    </div>
  );
}

function PullRequestChecksMeta(props: {
  readonly detail: PullRequestDetail;
  readonly failed: number;
}) {
  const label =
    props.failed > 0 ? `${props.failed} failing` : `${props.detail.checks.length} checks`;
  if (props.detail.checks.length === 0) {
    return <span className="ml-auto shrink-0 text-xs text-muted-foreground">{label}</span>;
  }
  return (
    <Popover>
      <PopoverTrigger
        className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded px-1.5 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
        aria-label={`Checks: ${label}`}
      >
        {props.failed > 0 ? (
          <CircleXIcon aria-hidden="true" className="size-3.5 text-destructive-foreground" />
        ) : (
          <CircleCheckIcon aria-hidden="true" className="size-3.5 text-success-foreground" />
        )}
        {label}
      </PopoverTrigger>
      <PopoverPopup align="end" className="w-80 max-w-[calc(100vw-2rem)] p-3">
        <p className="mb-2 font-medium text-sm">{label}</p>
        <ul className="flex flex-col gap-1">
          {keyedPullRequestChecks(props.detail.checks).map(({ check, key }) => (
            <li key={key} className="flex min-w-0 items-center gap-2 text-xs">
              <PullRequestCheckStatusIcon status={check.status} />
              <Tooltip>
                <TooltipTrigger render={<span className="min-w-0 flex-1 truncate" />}>
                  {check.name}
                </TooltipTrigger>
                <TooltipPopup side="top">{check.description ?? check.name}</TooltipPopup>
              </Tooltip>
              <span className="shrink-0 text-muted-foreground">{check.status}</span>
              {check.url === null ? null : (
                <a
                  className="shrink-0 text-primary hover:underline"
                  href={check.url}
                  rel="noreferrer"
                  target="_blank"
                >
                  Details
                </a>
              )}
            </li>
          ))}
        </ul>
      </PopoverPopup>
    </Popover>
  );
}

function PullRequestCheckStatusIcon(props: {
  readonly status: PullRequestDetail["checks"][number]["status"];
}) {
  if (props.status === "success") {
    return (
      <CircleCheckIcon aria-hidden="true" className="size-3.5 shrink-0 text-success-foreground" />
    );
  }
  if (props.status === "failure" || props.status === "cancelled") {
    return (
      <CircleXIcon aria-hidden="true" className="size-3.5 shrink-0 text-destructive-foreground" />
    );
  }
  return (
    <CircleDashedIcon aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
  );
}
